import { ArrowPathIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn, stopPropagation, TOOLBAR_BUTTON_CLASS } from './imageCarouselShared';
import {
    findPendingPreviewAction,
    isMenuNavigationKey,
    layoutPreviewActionMenu,
    nextMenuItemIndex,
    type MenuNavigationKey,
    type PreviewAction,
    type PreviewActionTone
} from './previewActionMenuModel';

const MORE_ACTIONS_LABEL = 'More actions';

const ITEM_TONE_CLASS: Record<PreviewActionTone, string> = {
    default: 'text-white hover:bg-white/15 focus-visible:bg-white/15',
    danger: 'text-red-400 hover:bg-red-500/15 hover:text-red-300 focus-visible:bg-red-500/15 focus-visible:text-red-300'
};

type CloseMenu = (restoreFocus: boolean) => void;
type ItemRefs = React.MutableRefObject<(HTMLButtonElement | null)[]>;

const focusNextItem = (items: (HTMLButtonElement | null)[], key: MenuNavigationKey) => {
    const current = items.findIndex((el) => el === document.activeElement);
    items[nextMenuItemIndex(current, items.length, key)]?.focus();
};

// Window capture runs before the carousel's and the files panel's window listeners,
// so Escape and the arrow keys stop here instead of closing the preview or full screen.
function useMenuKeyboard(open: boolean, itemRefs: ItemRefs, itemCount: number, close: CloseMenu) {
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                close(true);
            } else if (isMenuNavigationKey(e.key)) {
                e.preventDefault();
                e.stopPropagation();
                focusNextItem(itemRefs.current.slice(0, itemCount), e.key);
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.stopPropagation();
            }
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [open, itemRefs, itemCount, close]);
}

// Capture phase: the carousel toolbar stops mousedown from bubbling to the document.
function useOutsideClick(
    open: boolean,
    containerRef: React.RefObject<HTMLElement | null>,
    close: CloseMenu
) {
    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) close(false);
        };
        document.addEventListener('mousedown', onMouseDown, true);
        return () => document.removeEventListener('mousedown', onMouseDown, true);
    }, [open, containerRef, close]);
}

function usePreviewActionMenu(itemCount: number, busy: boolean) {
    const [requestedOpen, setRequestedOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const open = requestedOpen && !busy;

    const close = useCallback<CloseMenu>((restoreFocus) => {
        setRequestedOpen(false);
        if (restoreFocus) triggerRef.current?.focus();
    }, []);
    const toggle = useCallback(() => {
        if (!busy) setRequestedOpen((value) => !value);
    }, [busy]);

    // Close on a pending action so the menu doesn't reopen once it settles.
    useEffect(() => {
        if (busy) setRequestedOpen(false);
    }, [busy]);
    useEffect(() => {
        if (open) itemRefs.current[0]?.focus();
    }, [open]);
    useMenuKeyboard(open, itemRefs, itemCount, close);
    useOutsideClick(open, containerRef, close);

    return { open, close, toggle, containerRef, triggerRef, itemRefs };
}

interface PreviewActionMenuItemProps {
    action: PreviewAction;
    itemRef: (el: HTMLButtonElement | null) => void;
    onChoose: (action: PreviewAction) => void;
}

const PreviewActionMenuItem: React.FC<PreviewActionMenuItemProps> = ({
    action,
    itemRef,
    onChoose
}) => {
    const inactive = Boolean(action.disabled || action.pending);
    const Icon = action.icon;
    return (
        <button
            ref={itemRef}
            type="button"
            role="menuitem"
            tabIndex={-1}
            aria-disabled={inactive || undefined}
            onClick={() => {
                if (!inactive) onChoose(action);
            }}
            className={cn(
                'flex w-full items-center gap-2 h-8 px-2.5 rounded-md text-xs font-medium text-left whitespace-nowrap transition-colors focus:outline-none',
                inactive
                    ? 'text-white/50 cursor-not-allowed'
                    : ITEM_TONE_CLASS[action.tone ?? 'default']
            )}
        >
            <Icon className="w-4 h-4 shrink-0" />
            {action.label}
        </button>
    );
};

interface PreviewActionMenuPanelProps {
    id: string;
    actions: PreviewAction[];
    mirrored: boolean;
    itemRefs: ItemRefs;
    onChoose: (action: PreviewAction) => void;
}

const PreviewActionMenuPanel: React.FC<PreviewActionMenuPanelProps> = ({
    id,
    actions,
    mirrored,
    itemRefs,
    onChoose
}) => (
    <div
        id={id}
        role="menu"
        aria-label={MORE_ACTIONS_LABEL}
        className={cn(
            'absolute top-full mt-1.5 z-30 min-w-[12rem] p-1 bg-gray-900/95 text-white rounded-lg shadow-lg',
            mirrored ? 'left-0' : 'right-0'
        )}
        onDoubleClick={stopPropagation}
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
        onWheel={stopPropagation}
    >
        {layoutPreviewActionMenu(actions).map((entry) =>
            entry.kind === 'divider' ? (
                <div key={entry.key} role="separator" className="my-1 h-px bg-white/15" />
            ) : (
                <PreviewActionMenuItem
                    key={entry.action.id}
                    action={entry.action}
                    itemRef={(el) => {
                        itemRefs.current[entry.index] = el;
                    }}
                    onChoose={onChoose}
                />
            )
        )}
    </div>
);

interface PreviewActionMenuProps {
    actions: PreviewAction[];
    /** Toolbar sits on the left, so the menu hangs from the toolbar's left edge. */
    mirrored: boolean;
}

// aria-disabled, not disabled: focus returns to the trigger as an action starts and must stay there.
export const PreviewActionMenu: React.FC<PreviewActionMenuProps> = ({ actions, mirrored }) => {
    const pending = findPendingPreviewAction(actions);
    const menu = usePreviewActionMenu(actions.length, pending !== null);
    const menuId = useId();
    const triggerLabel = pending ? (pending.pendingLabel ?? pending.label) : MORE_ACTIONS_LABEL;
    const TriggerIcon = pending ? ArrowPathIcon : EllipsisHorizontalIcon;

    const choose = (action: PreviewAction) => {
        menu.close(true);
        action.onSelect();
    };

    return (
        <div ref={menu.containerRef} className="flex">
            <button
                ref={menu.triggerRef}
                type="button"
                onClick={menu.toggle}
                aria-disabled={pending !== null || undefined}
                aria-label={triggerLabel}
                title={triggerLabel}
                aria-haspopup="menu"
                aria-expanded={menu.open}
                aria-controls={menu.open ? menuId : undefined}
                aria-busy={pending !== null || undefined}
                className={cn(
                    TOOLBAR_BUTTON_CLASS,
                    'text-white aria-disabled:cursor-not-allowed aria-disabled:opacity-60'
                )}
            >
                <TriggerIcon className={cn('w-4 h-4', pending && 'animate-spin')} />
            </button>
            {menu.open && (
                <PreviewActionMenuPanel
                    id={menuId}
                    actions={actions}
                    mirrored={mirrored}
                    itemRefs={menu.itemRefs}
                    onChoose={choose}
                />
            )}
        </div>
    );
};
